/**
 * @file    widgetDeserializer.ts
 * @scope   SHARED
 * @summary Rebuilds the in-memory `EnterpriseGlobalModel` from a persisted widget `CustomConfig`
 *          (edit mode). Inverse of `widgetSerializer.ts`.
 * @consumes CustomConfig, FieldsManager/DataSource models, projectFrequency + operator helpers
 * @produces `initializeEnterpriseFromWidget` + stored-field extraction helpers
 * @sideEffects None — pure reconstruction.
 * @notes WHY defensive: reads the camelCase shape produced by normalizeWidget and stored configs that
 *        may predate current fields; keep it forgiving so old widgets still open.
 */
import {
  SelectedFieldModel,
  SelectedUserFields,
  FieldFilter,
  FilterLogicalOperator,
  NumericSettingsModel,
  NumericValueOperator,
} from 'models/Enterprise/FieldsManagerModel';
import { EnterpriseGlobalModel } from 'models/Enterprise/EnterpriseGlobalModel';
import { PartitionModel } from 'models/Enterprise/DataSourceModel';
import { CustomConfig } from 'models/WidgetModel';
import type { StoredCollection } from '../../../../engine/contracts';
import { deserializeWithDelta } from '../../../../engine/serialization/baseSerializer';
import {
  buildProjectFrequencyField,
  isStoredProjectFrequencyField,
} from '../payload/projectFrequency';
import { fromApiOperator } from '../payload/calculationPayload';

// ---------------------------------------------------------------------------
// Helpers for extracting stored (camelCase) properties
// ---------------------------------------------------------------------------

/**
 * Extracts values from a stored field using camelCase property names.
 */
const extractStoredField = (storedField: any) => {
  // Read rowType from metadata,
  // falling back to legacy IsL1Category property
  const rowType = storedField.metadata?.rowType ?? (storedField.isL1Category ? 'L1' : undefined);
  const isL1Category = rowType === 'L1' ? true : rowType === 'L2' ? false : false;

  return {
    type: storedField.type,
    displayName: storedField.displayName ?? '',
    decimalPrecision: storedField.decimalPrecision ?? 0,
    normalize: storedField.normalize ?? false,
    statTesting: storedField.statTesting ?? false,
    isL1Category: isL1Category,
    normalizeValues: storedField.normalizeValues ?? [],
    metadata: storedField.metadata ?? {},
    // Sub-objects
    base: storedField.base,
    avg: storedField.avg,
    stdDev: storedField.stdDev,
    valueCount: storedField.valueCount,
    totalMetric: storedField.totalMetric,
    detractors: storedField.detractors,
    neutrals: storedField.neutrals,
    promoters: storedField.promoters,
  };
};

/**
 * Extracts values from a sub-metric (base, avg, valueCount, etc.).
 */
const extractSubMetric = (metric: any) => {
  if (!metric) return null;
  return {
    fieldName: metric.fieldName ?? '',
    path: metric.path ?? '',
    displayName: metric.displayName ?? '',
    type: metric.type,
    values: metric.values ?? [],
    valueOperator: metric.valueOperator as NumericValueOperator | undefined,
  };
};

/**
 * Restores numeric settings from a stored ValueCount: present when a ValueOperator was saved,
 * or when the path is empty and every value is a number (numeric field with 'equals').
 */
const extractNumericSettings = (
  valueCount: ReturnType<typeof extractSubMetric>
): NumericSettingsModel | undefined => {
  if (!valueCount?.values.length) return undefined;
  const allNumbers = valueCount.values.every((v: unknown) => typeof v === 'number');
  if (!allNumbers || (!valueCount.valueOperator && valueCount.path !== '')) return undefined;
  return { valueOperator: valueCount.valueOperator ?? 'equals', values: valueCount.values };
};

/**
 * Converts a single stored FilterCondition (either the current camelCase shape or the
 * legacy PascalCase shape) into a UI FieldFilter. `logicalOperator` is filled in by the
 * caller once the condition's position in the flattened sequence is known.
 */
const mapConditionToFilter = (c: any): FieldFilter => {
  const values: any[] = c.values ?? [];
  const friendly: any[] = c.friendlyValues ?? [];
  const attributeName: string = c.attributeName ?? '';
  const attributeFriendlyName: string = c.attributeFriendlyName ?? '';
  const attributeKind: 'Scalar' | 'Collection' =
    String(c.attributeKind ?? 'scalar').toLowerCase() === 'collection' ? 'Collection' : 'Scalar';
  const subAttribute: string = c.subAttribute ?? '';
  const rawOperator: string = c.operator ?? '';
  return {
    id: crypto.randomUUID(),
    filterGuid: '',
    filterName: attributeFriendlyName || attributeName,
    fieldTypeName: '',
    attributePath: attributeName,
    attributeKind,
    subAttribute,
    operator: fromApiOperator(rawOperator),
    selectedValues: values.map((v: any, valueIndex: number) => {
      const label = String(friendly[valueIndex] ?? v);
      return {
        value: v,
        label,
        longText: label,
        raw: null,
      };
    }),
    selectedValueGuid: '',
    selectedValueLabel: '',
  };
};

/**
 * Flattens a (possibly nested) FilterGroup into an ordered list of filters, each paired with
 * the operator joining it to the PREVIOUS item in the sequence. Handles both the current
 * nested camelCase grammar (`logic`/`conditions`/`groups`) and the legacy flat PascalCase
 * shape (`Logic`/`Conditions`, no `groups`) — the latter is just a group with no nested groups.
 * Reverses buildFilterGroup: a group's own `conditions` come before its nested `groups` in the
 * flattened order, and a nested group's first item joins to the previous item via the PARENT
 * group's logic while its remaining items join via its OWN logic.
 */
const flattenFilterGroup = (
  group: any
): { filter: FieldFilter; joinLogic: FilterLogicalOperator }[] => {
  if (!group) return [];

  const groupLogicRaw = group.logic;
  const groupLogic: FilterLogicalOperator =
    String(groupLogicRaw).toLowerCase() === 'or' ? 'OR' : 'AND';
  const conditions: any[] = group.conditions ?? [];
  const nestedGroups: any[] = group.groups ?? [];

  const result: { filter: FieldFilter; joinLogic: FilterLogicalOperator }[] = conditions.map(
    (c) => ({ filter: mapConditionToFilter(c), joinLogic: groupLogic })
  );

  nestedGroups.forEach((sub) => {
    flattenFilterGroup(sub).forEach((item, idx) => {
      result.push(idx === 0 ? { ...item, joinLogic: groupLogic } : item);
    });
  });

  return result;
};

/**
 * Converts a stored FilterGroup payload back into UI filters.
 * Shared by metricFilter (field level) and partitionFilter (partition level).
 * @param storedFilter - The raw stored filter object, or undefined
 * @returns An object with filters and filterLogicalOperator, or empty defaults
 */
const deserializeFilterPayload = (
  storedFilter: any
): { filters: FieldFilter[]; filterLogicalOperator: FilterLogicalOperator } => {
  const flattened = flattenFilterGroup(storedFilter);
  if (flattened.length === 0) return { filters: [], filterLogicalOperator: 'AND' };

  const filters: FieldFilter[] = flattened.map((item, i) => ({
    ...item.filter,
    logicalOperator: i === 0 ? 'AND' : item.joinLogic,
  }));

  // Legacy top-level operator, kept only for backward-compat consumers of the deprecated field.
  const topLogicRaw = storedFilter?.logic;
  const filterLogicalOperator: FilterLogicalOperator =
    String(topLogicRaw).toLowerCase() === 'or' ? 'OR' : 'AND';

  return { filters, filterLogicalOperator };
};

/**
 * Restores filters and filterLogicalOperator from a stored metricFilter payload.
 * @param storedField - The raw stored field that may contain a metricFilter
 * @returns An object with filters and filterLogicalOperator, or empty defaults
 */
const deserializeMetricFilter = (
  storedField: any
): { filters: FieldFilter[]; filterLogicalOperator: FilterLogicalOperator } =>
  deserializeFilterPayload(storedField?.metricFilter);

/**
 * Restores partition-level filters (partitionFilter) from a stored queryCollection entry.
 * Mirrors deserializeMetricFilter but reads the partition-level payload so the result
 * can populate the PartitionFilters* properties on SelectedUserFields.
 * @param storedQueryCollection - The raw stored queryCollection entry that may contain a partitionFilter
 * @returns An object with filters and filterLogicalOperator, or empty defaults
 */
const deserializePartitionFilter = (
  storedQueryCollection: any
): { filters: FieldFilter[]; filterLogicalOperator: FilterLogicalOperator } =>
  deserializeFilterPayload(storedQueryCollection?.partitionFilter);

// ---------------------------------------------------------------------------
// Base builder
// ---------------------------------------------------------------------------

/** Builds the base SelectedFieldModel shared by all calculation types during edit-mode recovery. */
const buildBaseSelectedField = (
  index: number,
  columnName: string,
  path: string,
  displayName: string,
  reconstructedResponses: { rating: number }[],
  storedField: any
): SelectedFieldModel => {
  const extracted = extractStoredField(storedField);
  const { filters, filterLogicalOperator } = deserializeMetricFilter(storedField);
  return {
    fieldNumber: index + 1,
    field: {
      fieldGuid: '',
      fieldName: '',
      displayName,
      columnName: columnName,
      ...(path ? { path } : {}),
      responses: reconstructedResponses,
    },
    displayName: extracted.displayName,
    calculationType: {
      type: String(extracted.type),
      displayName: '',
      fieldName: '',
    },
    precision: String(extracted.decimalPrecision),
    normalize: extracted.normalize,
    statTesting: extracted.statTesting,
    isL1Category: extracted.isL1Category ?? false,
    ...(filters.length > 0 ? { filters, filterLogicalOperator } : {}),
  };
};

// ---------------------------------------------------------------------------
// Strategy — one deserializer per calculation type
// ---------------------------------------------------------------------------

interface StoredFieldContext {
  storedField: any;
  index: number;
  reconstructedResponses: { rating: number }[];
}

type FieldDeserializer = (ctx: StoredFieldContext) => SelectedFieldModel;

/** Mean — base is the canonical reference. */
const deserializeMean: FieldDeserializer = ({ storedField, index, reconstructedResponses }) => {
  const extracted = extractStoredField(storedField);
  const base = extractSubMetric(extracted.base);
  const avg = extractSubMetric(extracted.avg);

  const columnName = base?.fieldName ?? '';
  const path = base?.path ?? avg?.path ?? '';
  const displayName = base?.displayName ?? '';
  return buildBaseSelectedField(
    index,
    columnName,
    path,
    displayName,
    reconstructedResponses,
    storedField
  );
};

/** Percentage — base is canonical; valueCount shares the same fieldName. */
const deserializePercentage: FieldDeserializer = ({
  storedField,
  index,
  reconstructedResponses,
}) => {
  const extracted = extractStoredField(storedField);
  const base = extractSubMetric(extracted.base);
  const valueCount = extractSubMetric(extracted.valueCount);

  const columnName = base?.fieldName ?? '';
  const path = base?.path ?? '';
  const displayName = base?.displayName ?? '';
  const storedValues: any[] = valueCount?.values ?? [];

  const baseField = buildBaseSelectedField(
    index,
    columnName,
    path,
    displayName,
    reconstructedResponses,
    storedField
  );

  const numericSettings = extractNumericSettings(valueCount);
  if (numericSettings) return { ...baseField, numericSettings };

  if (storedValues.length > 0) {
    return {
      ...baseField,
      percentageSettings: {
        path: base?.path ?? '',
        selectedValues: storedValues.map((v: any) => ({ value: v, label: String(v) })),
      },
    };
  }
  return baseField;
};

/** NPS — base is canonical; detractors/neutrals/promoters share the same fieldName. */
const deserializeNps: FieldDeserializer = ({ storedField, index, reconstructedResponses }) => {
  const extracted = extractStoredField(storedField);
  const base = extractSubMetric(extracted.base);
  const promoters = extractSubMetric(extracted.promoters);
  const detractors = extractSubMetric(extracted.detractors);

  const columnName = base?.fieldName ?? '';
  const path = base?.path ?? promoters?.path ?? '';
  const displayName = base?.displayName ?? '';

  const baseField = buildBaseSelectedField(
    index,
    columnName,
    path,
    displayName,
    reconstructedResponses,
    storedField
  );

  const detractorValues: number[] = detractors?.values ?? [];
  const promoterValues: number[] = promoters?.values ?? [];
  if (detractorValues.length > 0 || promoterValues.length > 0) {
    return {
      ...baseField,
      npsSettings: {
        detractorsMax: detractorValues.length > 0 ? Math.max(...detractorValues) : 6,
        promotersMin: promoterValues.length > 0 ? Math.min(...promoterValues) : 9,
        path: base?.path ?? '',
      },
    };
  }
  return baseField;
};

/** TotalCount — totalMetric is the canonical reference. */
const deserializeTotalCount: FieldDeserializer = ({
  storedField,
  index,
  reconstructedResponses,
}) => {
  const extracted = extractStoredField(storedField);
  const totalMetric = extractSubMetric(extracted.totalMetric);
  const valueCount = extractSubMetric(extracted.valueCount);

  const columnName = totalMetric?.fieldName ?? '';
  const path = totalMetric?.path ?? '';
  const displayName = totalMetric?.displayName ?? '';
  const storedValues: any[] = valueCount?.values ?? [];

  const baseField = buildBaseSelectedField(
    index,
    columnName,
    path,
    displayName,
    reconstructedResponses,
    storedField
  );

  const numericSettings = extractNumericSettings(valueCount);
  if (numericSettings) return { ...baseField, numericSettings };

  if (storedValues.length > 0) {
    return {
      ...baseField,
      percentageSettings: {
        path: extracted?.base?.path ?? '',
        selectedValues: storedValues.map((v: any) => ({ value: v, label: String(v) })),
      },
    };
  }
  return baseField;
};

/** Fallback for unknown calculation types. */
const deserializeDefault: FieldDeserializer = ({ storedField, index, reconstructedResponses }) =>
  buildBaseSelectedField(index, '', '', '', reconstructedResponses, storedField);

/**
 * Strategy map: calculation type -> deserializer.
 * To support a new type, add an entry here — no switch/case modification needed (OCP).
 */
const FIELD_DESERIALIZERS: Record<string, FieldDeserializer> = {
  '1': deserializeMean,
  '2': deserializePercentage,
  '3': deserializeNps,
  '4': deserializeTotalCount,
  '6': deserializeMean,
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Reverses the extractEnterpriseDataToWidget payload mapping.
 * Converts a stored field from queryCollections back into a SelectedFieldModel.
 * @param storedField - A field entry as stored in queryCollections[n].fields
 * @param index - Position in the array, used to set fieldNumber
 * @returns A SelectedFieldModel populated from the stored payload
 */
export const mapStoredFieldToSelectedField = (
  storedField: any,
  index: number
): SelectedFieldModel => {
  if (isStoredProjectFrequencyField(storedField)) {
    return buildProjectFrequencyField(storedField?.displayName ?? '');
  }

  const extracted = extractStoredField(storedField);
  const reconstructedResponses = (
    Array.isArray(extracted.normalizeValues) ? extracted.normalizeValues : []
  ).map((rating: number) => ({ rating }));

  const deserialize = FIELD_DESERIALIZERS[String(extracted.type)] ?? deserializeDefault;

  return deserialize({ storedField, index, reconstructedResponses });
};

/** Slice returned when the widget has no persisted collections yet. */
export const emptyEnterpriseSlice = (): EnterpriseGlobalModel => ({
  options: { availableFieldsManagerOptions: [] },
  selectedValues: { selectedPartitions: [], selectedEnterpriseFields: [] },
});

/**
 * Enterprise-owned part of deserialization: rebuilds partitions + fields from collections that
 * already passed the engine identity check (non-empty `DataCollectionGuid`).
 * @param validCollections - Persisted collections with a DataCollectionGuid
 * @returns A populated EnterpriseGlobalModel
 */
export const deserializeEnterpriseCollections = (
  validCollections: StoredCollection[]
): EnterpriseGlobalModel => {
  const selectedPartitions: PartitionModel[] = validCollections.map((qc: any) => ({
    value: qc.dataCollectionGuid,
    label: '',
    originId: qc.originId || '',
  }));

  const selectedEnterpriseFields: SelectedUserFields[] = validCollections.map((qc: any) => {
    const { filters: partitionFilters, filterLogicalOperator: partitionFiltersLogicalOperator } =
      deserializePartitionFilter(qc);
    return {
      partitionGuid: qc.dataCollectionGuid,
      fields: Array.isArray(qc.fields) ? qc.fields.map(mapStoredFieldToSelectedField) : [],
      fromEditMode: true,
      ...(partitionFilters.length > 0
        ? {
            partitionFilters: partitionFilters,
            partitionFiltersLogicalOperator: partitionFiltersLogicalOperator,
          }
        : {}),
    };
  });

  return {
    options: { availableFieldsManagerOptions: [] },
    selectedValues: { selectedPartitions, selectedEnterpriseFields },
  };
};

/**
 * Builds an initial EnterpriseGlobalModel from an existing widget's queryCollections.
 * Used when re-opening the drawer for an already-configured enterprise widget,
 * so that selectedPartitions and selectedEnterpriseFields are pre-populated.
 * @param customWidgetConfig - The existing custom widget configuration
 * @returns A populated EnterpriseGlobalModel derived from the stored queryCollections
 */
export const initializeEnterpriseFromWidget = (
  customWidgetConfig: CustomConfig | undefined
): EnterpriseGlobalModel =>
  deserializeWithDelta(customWidgetConfig, {
    deserializeCollections: deserializeEnterpriseCollections,
    emptySlice: emptyEnterpriseSlice,
  });
